import { z } from 'zod';
import { PublicTicketSheetSchema } from './ticket-sheet';
import { Dateish, IdCoerce, PG_INT_MAX, PaginationQuerySchema } from './common';

// ── Gestão staff: usuários do portal por cliente (/clients/:id/users) ───────
export const ClientUserSchema = z.object({
  userId: z.number().int(),
  name: z.string(),
  email: z.string(),
  clientId: z.number().int(),
});
export type ClientUser = z.infer<typeof ClientUserSchema>;

/**
 * POST /clients/:id/users em AUTH_MODE=local — cria user role `client` (senha
 * local) OU vincula um user client existente pelo e-mail (aí name/password são
 * ignorados). `clientIds` extras além do :id da rota são vinculados juntos.
 */
export const ClientUserCreateBodySchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  password: z.string().min(8),
  clientIds: z.array(z.number().int().positive()).optional(),
});
export type ClientUserCreateBody = z.infer<typeof ClientUserCreateBodySchema>;

/**
 * O MESMO endpoint em AUTH_MODE=keycloak. A senha não é nossa: quem guarda
 * credencial é o realm, e a conta precisa existir lá (com a realm role
 * `client`) para o login acontecer. Por isso o campo não é opcional — é
 * PROIBIDO: `.strict()` faz um `password` no corpo virar 422, em vez de a API
 * aceitar em silêncio um segredo que não guarda em lugar nenhum e devolver 201
 * como se a senha tivesse sido definida. Rejeitar é o honesto; ignorar mentiria
 * para quem chamou.
 */
export const ClientUserKeycloakCreateBodySchema = z
  .object({
    name: z.string().min(1),
    email: z.string().email(),
    clientIds: z.array(z.number().int().positive()).optional(),
  })
  .strict();
export type ClientUserKeycloakCreateBody = z.infer<typeof ClientUserKeycloakCreateBodySchema>;

// ── Spec 034: os clientes de cada login de portal, pela tela de usuários ────

/** Um vínculo login ↔ cliente deste tenant, com o nome do cliente. */
export const UserClientLinkSchema = z.object({
  userId: z.number().int(),
  clientId: z.number().int(),
  clientName: z.string(),
});
export type UserClientLink = z.infer<typeof UserClientLinkSchema>;

/**
 * `GET /users/client-links?userIds=1,2,3` — os clientes dos logins da PÁGINA
 * da tela de usuários (a coluna "Cliente"), no máximo uma página (200).
 */
export const UserClientLinksQuerySchema = z.object({
  userIds: z
    .string()
    .transform((s) =>
      s
        .split(',')
        .map((x) => Number(x.trim()))
        .filter((n) => Number.isInteger(n) && n > 0 && n <= PG_INT_MAX)
    )
    .pipe(z.array(z.number().int().positive()).min(1).max(200)),
});

/** `POST /users/:id/clients` — vincula o login a mais um cliente deste tenant. */
export const UserClientAddBodySchema = z.object({ clientId: IdCoerce }).strict();
export type UserClientAddBody = z.infer<typeof UserClientAddBodySchema>;

/** `DELETE /users/:id/clients/:clientId` */
export const UserClientParamSchema = z.object({ id: IdCoerce, clientId: IdCoerce });

// ── Portal (role client) ────────────────────────────────────────────────────
export const PortalDeskSchema = z.object({
  id: z.number().int(),
  displayName: z.string(),
  icon: z.string().nullable(),
  /** texto pré-preenchido na descrição do PortalNewTicket ao escolher a mesa (v5) */
  openingTemplate: z.string().nullable(),
});
export type PortalDesk = z.infer<typeof PortalDeskSchema>;

/** GET /portal/summary — tickets abertos por mesa (só mesas com tickets). */
export const PortalSummarySchema = z.object({
  openByDesk: z.array(
    z.object({
      deskId: z.number().int(),
      deskName: z.string(),
      open: z.number().int(),
    })
  ),
  totalOpen: z.number().int(),
});
export type PortalSummary = z.infer<typeof PortalSummarySchema>;

export const PortalTicketListQuerySchema = PaginationQuerySchema.extend({
  status: z.enum(['open', 'closed', 'canceled', 'all']).default('all'),
  search: z.string().optional(),
});
export type PortalTicketListQuery = z.infer<typeof PortalTicketListQuerySchema>;

/** Subconjunto seguro do ticket — nada de campos internos/SLA. */
export const PortalTicketListItemSchema = z.object({
  ticketNumber: z.number().int(),
  title: z.string(),
  deskName: z.string(),
  stageName: z.string(),
  statusName: z.string(),
  statusKind: z.enum(['open', 'closed', 'canceled', 'custom']),
  createdAt: Dateish,
  closedAt: Dateish.nullable(),
});
export type PortalTicketListItem = z.infer<typeof PortalTicketListItemSchema>;

/** Comentário PÚBLICO (internos jamais chegam ao portal). */
export const PortalCommentSchema = z.object({
  id: z.number().int(),
  authorName: z.string(),
  body: z.string(),
  createdAt: Dateish,
});
export type PortalComment = z.infer<typeof PortalCommentSchema>;

/** Canal da empresa para o cliente falar sobre um atendimento (org_settings). */
export const PortalContactSchema = z.object({
  name: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
});
export type PortalContact = z.infer<typeof PortalContactSchema>;

export const PortalTicketDetailSchema = PortalTicketListItemSchema.extend({
  description: z.string().nullable(),
  /**
   * A ficha do associado (spec 031, D6): publicTicketSheet — SÓ a correção da
   * equipe e a descrição, nunca o solicitante nem o recurso. null = o ticket
   * não tem ficha. Ausente = API anterior à spec 031 (a tela lê a descrição,
   * como antes).
   */
  sheet: PublicTicketSheetSchema.nullable().optional(),
  comments: z.array(PortalCommentSchema),
  /**
   * O cliente não pode comentar: ticket fechado/cancelado ou de mesa espelhada
   * do TiFlux (spec 011). Até 2026-09-28 o portal oferecia o botão mesmo assim
   * e todo envio falhava com a mensagem interna "edite no TiFlux".
   */
  readOnly: z.boolean(),
  /** como falar com a empresa quando readOnly; null = nada configurado */
  contact: PortalContactSchema.nullable(),
});
export type PortalTicketDetail = z.infer<typeof PortalTicketDetailSchema>;

/**
 * POST /portal/tickets — mesa precisa estar portal_enabled. `clientId` só é
 * exigido quando o user está vinculado a mais de um cliente.
 */
export const PortalNewTicketBodySchema = z.object({
  deskId: z.number().int().positive(),
  title: z.string().min(1),
  description: z.string().min(1),
  clientId: z.number().int().positive().optional(),
});
export type PortalNewTicketBody = z.infer<typeof PortalNewTicketBodySchema>;

/** POST /portal/tickets/:n/comments — sempre público, ticket aberto. */
export const PortalCommentBodySchema = z.object({
  body: z.string().min(1),
});
export type PortalCommentBody = z.infer<typeof PortalCommentBodySchema>;
